import React, { useEffect, useRef } from 'react';
import type { SVGProps } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { PageMeta } from '@/components/PageMeta';
import { ROUTES } from '@/config/routes';
import { SUBJECTS } from '@/lib/validation/contact.schema';
import { useContactStore } from '@/stores/contact-store';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { AlertTriangle, CheckCircle2, ChevronDown, Headphones, Info, MessageCircle, Send } from 'lucide-react';

import { Facebook, Youtube } from '../components/ui/social-icons';

type IconComponent = React.ComponentType<SVGProps<SVGSVGElement>>;

const SITE_KEY = import.meta.env.VITE_HCAPTCHA_SITE_KEY as string | undefined;

export default function Contact() {
  const [searchParams, setSearchParams] = useSearchParams();

  const { values, errors, isSubmitting, isSuccess, serverError, setField, setCaptchaToken, submit, reset } =
    useContactStore();

  const captchaRef = useRef<HCaptcha>(null);

  const inputBase =
    'w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition';
  const inputOk = `${inputBase} border-slate-200`;
  const inputErr = `${inputBase} border-red-300 ring-1 ring-red-200`;

  // Prefill depuis /contact?subject=...&message=...
  useEffect(() => {
    const subjectParam = searchParams.get('subject');
    const messageParam = searchParams.get('message');

    if (!subjectParam && !messageParam) return;

    if (subjectParam) {
      // IMPORTANT: le <select> est borné par SUBJECTS (z.enum),
      // donc on ne set que si c'est une valeur autorisée.
      if (SUBJECTS.includes(subjectParam as (typeof SUBJECTS)[number])) {
        setField('subject', subjectParam as (typeof SUBJECTS)[number]);
      }
    }

    if (messageParam) {
      setField('message', messageParam);
    }

    // UX: scroll vers le formulaire si on arrive avec des params (CTA)
    document.getElementById('formulaire')?.scrollIntoView({ behavior: 'smooth', block: 'start' });

    // Optionnel: nettoyer l'URL après prefill (URL propre, évite re-apply au refresh)
    // setSearchParams({}, { replace: true });

    // volontairement au mount uniquement
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await submit();

    // si erreur -> reset captcha pour éviter token “consommé”
    const { serverError: errNow, errors: fieldErrorsNow, isSuccess: okNow } = useContactStore.getState();

    if (!okNow && (errNow || fieldErrorsNow.captchaToken)) {
      captchaRef.current?.resetCaptcha();
      setCaptchaToken('');
    }
  };

  return (
    <div className='bg-slate-50 text-slate-900 font-sans selection:bg-blue-100'>
      <PageMeta
        title='Contact'
        description="Contactez l'équipe BoulesConnect : support technique, demande de démo, partenariat. Réponse garantie sous 24h."
      />

      {/* --- HERO SECTION --- */}
      <section className='pt-40 pb-20 bg-linear-to-br from-blue-600 via-indigo-600 to-purple-600 text-white px-6'>
        <div className='max-w-7xl mx-auto text-center'>
          <div className='inline-flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full text-sm font-semibold mb-6 backdrop-blur-sm'>
            <Headphones className='w-4 h-4' />
            Support Technique disponible 7j/7
          </div>

          <h1 className='text-5xl md:text-6xl font-extrabold mb-6 leading-tight'>
            Besoin d&apos;aide ?
            <br />
            <span className='text-blue-100'>Contactez-nous !</span>
          </h1>

          <p className='text-xl text-blue-100 max-w-2xl mx-auto'>
            Notre équipe est à votre écoute pour répondre à toutes vos questions sur BoulesConnect.
          </p>
        </div>
      </section>

      {/* --- MAIN CONTENT --- */}
      <section className='py-20 px-6'>
        <div className='max-w-7xl mx-auto'>
          <div className='grid md:grid-cols-2 gap-12'>
            {/* CENTRE D'AIDE / SUPPORT (pré-contact) */}
            <div className='md:col-span-2'>
              <div className='bg-white rounded-3xl border border-slate-200 p-8 shadow-sm'>
                <div className='flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6'>
                  <div className='flex items-start gap-4'>
                    <div className='w-12 h-12 rounded-2xl bg-blue-50 border border-blue-100 flex items-center justify-center shrink-0'>
                      <MessageCircle className='w-6 h-6 text-blue-600' />
                    </div>
                    <div>
                      <p className='text-xs font-black uppercase tracking-wider text-blue-700'>
                        Avant de nous contacter
                      </p>
                      <h2 className='text-2xl font-extrabold text-slate-900 mt-1'>Consultez le Centre d’aide</h2>
                      <p className='text-slate-600 mt-2 leading-relaxed'>
                        La plupart des questions ont déjà une réponse : création de concours, gestion de club,
                        inscriptions, paiements…
                        <br />
                        Gagnez du temps en vérifiant d’abord les articles du support.
                      </p>
                    </div>
                  </div>

                  <div className='flex flex-col sm:flex-row gap-3'>
                    <Link
                      to={ROUTES.SUPPORT}
                      className='inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-extrabold bg-blue-600 hover:bg-blue-700 text-white transition transform hover:-translate-y-0.5 shadow-lg shadow-blue-200'
                    >
                      <Headphones className='w-5 h-5' />
                      Aller au Centre d’aide
                      <ChevronDown className='w-4 h-4 -rotate-90 opacity-90' />
                    </Link>

                    <a
                      href='#formulaire'
                      className='inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-extrabold bg-slate-50 hover:bg-slate-100 text-slate-900 border border-slate-200 transition transform hover:-translate-y-0.5'
                    >
                      <Send className='w-5 h-5 text-slate-700' />
                      Contacter le support
                    </a>
                  </div>
                </div>

                <div className='mt-6 grid sm:grid-cols-3 gap-3'>
                  <Link
                    to={ROUTES.SUPPORT}
                    className='group rounded-2xl border border-slate-200 bg-slate-50 p-4 hover:bg-white hover:border-slate-300 transition'
                  >
                    <p className='text-sm font-extrabold text-slate-900 group-hover:text-blue-700'>Concours</p>
                    <p className='text-xs text-slate-600 mt-1'>Créer un concours, concours payants, restrictions</p>
                  </Link>
                  <Link
                    to={ROUTES.SUPPORT}
                    className='group rounded-2xl border border-slate-200 bg-slate-50 p-4 hover:bg-white hover:border-slate-300 transition'
                  >
                    <p className='text-sm font-extrabold text-slate-900 group-hover:text-blue-700'>Clubs</p>
                    <p className='text-xs text-slate-600 mt-1'>Devenir admin, ajouter des membres, affiliations</p>
                  </Link>
                  <Link
                    to={ROUTES.SUPPORT}
                    className='group rounded-2xl border border-slate-200 bg-slate-50 p-4 hover:bg-white hover:border-slate-300 transition'
                  >
                    <p className='text-sm font-extrabold text-slate-900 group-hover:text-blue-700'>Paiements</p>
                    <p className='text-xs text-slate-600 mt-1'>Inscription, paiement en ligne, remboursements</p>
                  </Link>
                </div>
              </div>
            </div>
            {/* FORMULAIRE DE CONTACT */}
            <div id='formulaire'>
              <h2 className='text-3xl font-bold text-slate-900 mb-4'>Envoyez-nous un message</h2>
              <p className='text-slate-600 mb-8'>
                Remplissez le formulaire ci-dessous et nous vous répondrons dans les plus brefs délais.
              </p>

              <form
                onSubmit={onSubmit}
                className='space-y-6 bg-white p-8 rounded-3xl border border-slate-200 shadow-sm'
              >
                {/* Success banner */}
                {isSuccess && (
                  <div className='rounded-2xl border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3'>
                    <CheckCircle2 className='w-5 h-5 text-emerald-600 shrink-0 mt-0.5' />
                    <div>
                      <p className='font-bold text-emerald-900'>Message envoyé</p>
                      <p className='text-sm text-emerald-800'>Merci. Nous revenons vers vous rapidement.</p>
                      <button
                        type='button'
                        onClick={() => {
                          reset();
                          captchaRef.current?.resetCaptcha();
                          setCaptchaToken('');
                          // Optionnel: nettoyer l'URL (si elle contenait des params)
                          setSearchParams({}, { replace: true });
                        }}
                        className='mt-2 text-sm font-bold text-emerald-900 underline'
                      >
                        Envoyer un autre message
                      </button>
                    </div>
                  </div>
                )}

                {/* Server error banner */}
                {serverError && (
                  <div className='rounded-2xl border border-red-200 bg-red-50 p-4 flex items-start gap-3'>
                    <AlertTriangle className='w-5 h-5 text-red-600 shrink-0 mt-0.5' />
                    <div>
                      <p className='font-bold text-red-900'>Impossible d’envoyer le message</p>
                      <p className='text-sm text-red-800'>{serverError}</p>
                    </div>
                  </div>
                )}

                <div className='grid sm:grid-cols-2 gap-6'>
                  <div>
                    <label className='block text-sm font-semibold text-slate-900 mb-2'>Nom complet *</label>
                    <input
                      type='text'
                      placeholder='Jean Dupont'
                      className={errors.fullName ? inputErr : inputOk}
                      value={values.fullName}
                      onChange={e => setField('fullName', e.target.value)}
                      disabled={isSubmitting}
                      autoComplete='name'
                    />
                    {errors.fullName && <p className='text-xs text-red-600 mt-1 font-medium'>{errors.fullName}</p>}
                  </div>

                  <div>
                    <label className='block text-sm font-semibold text-slate-900 mb-2'>Email *</label>
                    <input
                      type='email'
                      placeholder='jean.dupont@email.fr'
                      className={errors.email ? inputErr : inputOk}
                      value={values.email}
                      onChange={e => setField('email', e.target.value)}
                      disabled={isSubmitting}
                      autoComplete='email'
                    />
                    {errors.email && <p className='text-xs text-red-600 mt-1 font-medium'>{errors.email}</p>}
                  </div>
                </div>

                <div>
                  <label className='block text-sm font-semibold text-slate-900 mb-2'>Téléphone</label>
                  <input
                    type='tel'
                    placeholder='06 12 34 56 78'
                    className={errors.phone ? inputErr : inputOk}
                    value={values.phone ?? ''}
                    onChange={e => setField('phone', e.target.value)}
                    disabled={isSubmitting}
                    autoComplete='tel'
                  />
                  {errors.phone && <p className='text-xs text-red-600 mt-1 font-medium'>{errors.phone}</p>}
                </div>

                <div>
                  <label className='block text-sm font-semibold text-slate-900 mb-2'>Sujet *</label>
                  <select
                    className={errors.subject ? inputErr : `${inputOk} appearance-none bg-white`}
                    value={values.subject}
                    onChange={e => setField('subject', e.target.value as (typeof SUBJECTS)[number])}
                    disabled={isSubmitting}
                    required
                  >
                    {SUBJECTS.map(s => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                  {errors.subject && <p className='text-xs text-red-600 mt-1 font-medium'>{errors.subject}</p>}
                </div>

                <div>
                  <label className='block text-sm font-semibold text-slate-900 mb-2'>Message *</label>
                  <textarea
                    rows={6}
                    placeholder='Décrivez votre demande...'
                    className={errors.message ? `${inputErr} resize-none` : `${inputOk} resize-none`}
                    value={values.message}
                    onChange={e => setField('message', e.target.value)}
                    disabled={isSubmitting}
                    required
                  />
                  {errors.message && <p className='text-xs text-red-600 mt-1 font-medium'>{errors.message}</p>}
                </div>

                {/* CAPTCHA */}
                {SITE_KEY && (
                  <div className='flex flex-col items-center gap-2'>
                    <HCaptcha
                      ref={captchaRef}
                      sitekey={SITE_KEY}
                      onVerify={token => setCaptchaToken(token)}
                      onExpire={() => setCaptchaToken('')}
                    />
                    {errors.captchaToken && (
                      <p className='text-xs text-red-600 mt-1 font-medium'>{errors.captchaToken}</p>
                    )}
                  </div>
                )}

                <button
                  type='submit'
                  disabled={isSubmitting}
                  className='w-full px-8 py-4 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-xl font-bold transition-all transform hover:-translate-y-1 flex items-center justify-center gap-2 shadow-lg shadow-blue-200 cursor-pointer'
                >
                  <Send className='w-5 h-5' />
                  {isSubmitting ? 'Envoi...' : 'Envoyer le message'}
                </button>

                <p className='text-xs text-slate-500 text-center'>
                  En envoyant ce formulaire, vous acceptez que vos données soient utilisées pour vous recontacter.
                </p>
              </form>
            </div>

            {/* INFORMATIONS DE CONTACT */}
            <div className='space-y-8'>
              {/* Réseaux Sociaux */}
              <div className='bg-white rounded-3xl border border-slate-200 p-8'>
                <h3 className='text-xl font-bold text-slate-900 mb-6'>Suivez-nous</h3>
                <div className='grid grid-cols-2 gap-4'>
                  <SocialLink
                    Icon={Facebook}
                    label='Facebook'
                    color='hover:border-blue-500 hover:bg-blue-50 text-blue-600'
                    link={`https://www.facebook.com/boulesconnect/`}
                  />
                  <SocialLink
                    Icon={Youtube}
                    label='Youtube'
                    color='hover:border-red-500 hover:bg-red-50 text-red-600'
                    link={`https://www.youtube.com/@BoulesConnect`}
                  />
                </div>
              </div>

              {/* Horaires */}
              <div className='bg-white rounded-3xl border border-slate-200 p-8'>
                <h3 className='text-xl font-bold text-slate-900 mb-4'>Horaires du support</h3>
                <div className='space-y-3'>
                  <ScheduleRow day='Lundi - Vendredi' time='9h00 - 18h00' />
                  <ScheduleRow day='Samedi' time='10h00 - 16h00' />
                  <ScheduleRow day='Dimanche' time='Urgence uniquement' isUrgent />
                </div>

                <div className='mt-6 p-4 bg-blue-50 rounded-xl flex items-start gap-3'>
                  <Info className='w-5 h-5 text-blue-600 shrink-0 mt-0.5' />
                  <p className='text-sm text-blue-900'>
                    Pour les urgences en dehors des horaires, utilisez le chat en ligne.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- FAQ SECTION --- */}
      <section className='py-20 bg-white px-6'>
        <div className='max-w-4xl mx-auto'>
          <div className='text-center mb-16'>
            <h2 className='text-4xl font-bold text-slate-900 mb-4'>Questions fréquentes</h2>
            <p className='text-xl text-slate-600'>Trouvez rapidement des réponses à vos questions</p>
          </div>

          <div className='space-y-4'>
            <FAQItem
              question='Comment créer un compte organisateur ?'
              answer="Pour créer un compte organisateur, cliquez sur 'S'inscrire' en haut de la page, puis sélectionnez 'Je suis organisateur'. Remplissez le formulaire avec vos informations et validez votre email. Vous pourrez ensuite créer vos premiers concours !"
            />
            <FAQItem
              question='Quels moyens de paiement sont acceptés ?'
              answer='Nous acceptons les cartes bancaires (Visa, Mastercard, American Express), PayPal, et les virements bancaires pour les inscriptions de clubs. Tous les paiements sont sécurisés via Stripe.'
            />
            <FAQItem
              question='Puis-je annuler mon inscription à un concours ?'
              answer="Oui, vous pouvez annuler votre inscription jusqu'à 7 jours avant le concours pour un remboursement complet. Au-delà de ce délai, les conditions dépendent de l'organisateur."
            />
          </div>

          <div className='text-center mt-12'>
            <p className='text-slate-600 mb-4'>Vous ne trouvez pas la réponse à votre question ?</p>
            <div className='flex flex-col sm:flex-row items-center justify-center gap-3'>
              <Link
                to={ROUTES.SUPPORT}
                className='inline-flex items-center gap-2 px-6 py-3 bg-slate-900 hover:bg-slate-950 text-white rounded-xl font-extrabold transition transform hover:-translate-y-1'
              >
                <Headphones className='w-5 h-5' />
                Explorer le Centre d’aide
              </Link>
              <a
                href={`#formulaire`}
                className='inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-extrabold transition transform hover:-translate-y-1'
              >
                <MessageCircle className='w-5 h-5' />
                Contactez-nous via le formulaire
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

// --- SOUS-COMPOSANTS ---

function SocialLink({ Icon, label, color, link }: { Icon: IconComponent; label: string; color: string; link: string }) {
  return (
    <a
      href={link}
      target='_blank'
      rel='noreferrer'
      className={`flex items-center gap-3 p-4 border border-slate-200 rounded-2xl transition-all group ${color}`}
    >
      <Icon className='w-5 h-5 transition-transform group-hover:scale-110' />
      <span className='font-bold text-slate-900'>{label}</span>
    </a>
  );
}

function ScheduleRow({ day, time, isUrgent = false }: { day: string; time: string; isUrgent?: boolean }) {
  return (
    <div className='flex items-center justify-between py-3 border-b border-slate-100 last:border-0'>
      <span className='text-slate-600 font-medium'>{day}</span>
      <span className={`font-bold ${isUrgent ? 'text-emerald-600' : 'text-slate-900'}`}>{time}</span>
    </div>
  );
}

function FAQItem({ question, answer }: { question: string; answer: string }) {
  return (
    <details className='group bg-slate-50 rounded-2xl border border-slate-200 overflow-hidden transition-all duration-300'>
      <summary className='flex items-center justify-between p-6 cursor-pointer hover:bg-slate-100 transition list-none'>
        <h3 className='font-bold text-slate-900 text-lg'>{question}</h3>
        <ChevronDown className='w-5 h-5 text-slate-500 group-open:rotate-180 transition-transform duration-300' />
      </summary>
      <div className='px-6 pb-6 text-slate-600 leading-relaxed animate-in fade-in slide-in-from-top-2'>{answer}</div>
    </details>
  );
}
